import { TLiveSiteParams } from '@wix/business-manager-api';
import React, { createContext, FC, useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import {
  BasicGenericScenarioParams,
  Orientations,
  Templates,
  VideoScripts,
} from '../useVideoMaker/videoMaker.types';
import { IVideoTemplate, VideoTemplates } from './VideoTemplates';

const typeForCustomizeVideoFields = (type: string) =>
  type === 'share-an-exciting-update' ? 'whats-new' : type;

const getSiteUrlWithoutProtocolPrefix = (url: string) => {
  return url.replace(/(^\w+:|^)\/\//, '').replace(/\/+$/, '');
};

const VideoMakerTemplatesContext = createContext<{
  getTemplateData: (
    template: IVideoTemplate,
  ) => Partial<BasicGenericScenarioParams>;
  findTemplateByType: (type: string) => IVideoTemplate;
}>({
  getTemplateData: () => ({}),
  findTemplateByType: () => VideoTemplates[0],
});

export const VideoMakerTemplatesProvider: FC<{
  liveSite: TLiveSiteParams | undefined;
  debug: boolean;
}> = ({ liveSite, debug, children }) => {
  const { formatMessage } = useIntl();

  const websiteUrl =
    liveSite &&
    liveSite.isSitePublished &&
    getSiteUrlWithoutProtocolPrefix(liveSite.viewUrl)
      ? getSiteUrlWithoutProtocolPrefix(liveSite.viewUrl)
      : formatMessage({
          id: 'video-maker-home.video-maker.widget.default-closing-text',
        });

  const findTemplateByType = useCallback(
    (type: string) =>
      VideoTemplates.find((template) => template.type === type) ||
      VideoTemplates[0],
    [VideoTemplates],
  );

  const getTemplateData = useCallback(
    ({
      type,
      sources,
      thumbnailUrl,
      videoUrls,
      font,
      primaryColor,
      secondaryColor,
      themeId,
      trackId,
      customClosingText,
      orientation = Orientations.Landscape,
    }): Partial<BasicGenericScenarioParams> => ({
      entityId: type,
      debug,
      template: Templates.Basic,
      templateData: {
        subTitle: formatMessage({
          id: 'video-maker-home.video-maker.widgets.subtitle',
        }),
        thumbnailUrl: thumbnailUrl[orientation],
        title: formatMessage({
          id: `video-maker-home.video-maker.widget.${type}.title`,
        }),
        videoUrl: videoUrls[orientation],
      },

      videoData: {
        script: VideoScripts.GENERAL_TEMPLATE,
        business: {
          font,
          primaryColor,
          secondaryColor,
          websiteOrEmail: websiteUrl,
        },
        themeId,
        trackId,
        scriptData: {
          closingText: customClosingText
            ? formatMessage({
                id: `video-maker-home.video-maker.widget.${typeForCustomizeVideoFields(
                  type,
                )}.closing-text`,
              })
            : websiteUrl,
          mainMessage: formatMessage({
            id: `video-maker-home.video-maker.widget.${typeForCustomizeVideoFields(
              type,
            )}.main-message`,
          }),
          openingText: formatMessage({
            id: `video-maker-home.video-maker.widget.${typeForCustomizeVideoFields(
              type,
            )}.opening-text`,
          }),
          secondaryMessage: formatMessage({
            id: `video-maker-home.video-maker.widget.${typeForCustomizeVideoFields(
              type,
            )}.secondary-message`,
          }),
        },
        sources,
        orientation,
      },
    }),
    [websiteUrl, debug],
  );

  return (
    <VideoMakerTemplatesContext.Provider
      value={{
        getTemplateData,
        findTemplateByType,
      }}
    >
      {children}
    </VideoMakerTemplatesContext.Provider>
  );
};

export const useVideoMakerTemplates = () =>
  useContext(VideoMakerTemplatesContext);
